/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: FaxViewerModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Virtual Faxes module.
----------------------------------------------------------------*/


import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X as XIcon } from 'lucide-react';

interface FaxViewerModalProps {
  blobUrl: string;
  title: string;
  onClose: () => void;
}

export default function FaxViewerModal({ blobUrl, title, onClose }: FaxViewerModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 99999 }}
      className="flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        style={{ width: '92vw', height: '92vh', maxWidth: '1200px', display: 'flex', flexDirection: 'column' }}
        className="bg-background rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground truncate min-w-0">
            <FileText className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="truncate">{title}</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-2 p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* PDF Viewer */}
        <iframe
          src={blobUrl}
          style={{ flex: 1, width: '100%', border: 'none' }}
          title="Fax Preview"
        />
      </div>
    </div>,
    document.body
  );
}
